import { useLayoutEffect, useRef, useState, type PointerEvent, type ReactNode } from 'react';
import { AddOutlined, CenterFocusStrongOutlined, RemoveOutlined } from '@mui/icons-material';
import { Box, IconButton, Stack, Tooltip } from '@mui/material';
import { designTokens } from '../../../design-system';

const maximumHeight = 320;
const minimumScale = 0.1;
const maximumScale = 2;

/** 순차·병렬 결재 도면의 배율과 탐색만 소유하며, 단계와 처리 권한은 소비자가 결정합니다. */
export function WorkflowApprovalFlowViewport({ children }: { children: ReactNode }) {
  const [view, setView] = useState({ fit: true, scale: 1 });
  const [dragging, setDragging] = useState(false);
  const [size, setSize] = useState({ viewportWidth: 0, width: 0, height: 0 });
  const viewportRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const anchor = useRef<{ x: number; y: number } | null>(null);
  const pan = useRef<{ pointerId: number; x: number; y: number; left: number; top: number } | null>(null);
  const ready = size.viewportWidth > 0 && size.width > 0 && size.height > 0;
  // 맞춤은 수동 축소 하한보다 작아져도 모든 단계를 보여줍니다.
  const fittedScale = ready ? Math.min(1, size.viewportWidth / size.width, maximumHeight / size.height) : 1;
  const scale = view.fit ? fittedScale : view.scale;
  const percentage = Math.max(1, Math.round(scale * 100));
  const canPan = ready && (Math.ceil(size.width * scale) > size.viewportWidth || Math.ceil(size.height * scale) > maximumHeight);

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const content = contentRef.current;
    if (!viewport || !content) return;
    const measure = () => {
      // 접힌 결재선의 0 크기로 기존 배율을 덮어쓰지 않습니다.
      if (!viewport.clientWidth || !content.offsetWidth || !content.offsetHeight) return;
      const next = { viewportWidth: viewport.clientWidth, width: content.offsetWidth, height: content.offsetHeight };
      setSize(current => current.viewportWidth === next.viewportWidth && current.width === next.width && current.height === next.height ? current : next);
    };
    const observer = new ResizeObserver(measure);
    observer.observe(viewport);
    observer.observe(content);
    measure();
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    if (view.fit) viewport.scrollTo({ left: 0, top: 0 });
    else if (anchor.current) {
      const leftInset = Math.max(0, (viewport.clientWidth - size.width * scale) / 2);
      viewport.scrollTo({
        left: anchor.current.x * scale + leftInset - viewport.clientWidth / 2,
        top: anchor.current.y * scale - viewport.clientHeight / 2,
      });
    }
    anchor.current = null;
  }, [scale, view.fit, size.width]);

  const zoomTo = (nextScale: number) => {
    const viewport = viewportRef.current;
    if (viewport) {
      const leftInset = Math.max(0, (viewport.clientWidth - size.width * scale) / 2);
      anchor.current = {
        x: (viewport.scrollLeft + viewport.clientWidth / 2 - leftInset) / scale,
        y: (viewport.scrollTop + viewport.clientHeight / 2) / scale,
      };
    }
    setView({ fit: false, scale: Math.max(minimumScale, Math.min(maximumScale, nextScale)) });
  };

  const endPan = (event: PointerEvent<HTMLDivElement>) => {
    if (pan.current?.pointerId !== event.pointerId) return;
    pan.current = null;
    setDragging(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  };

  return <Box sx={{ minWidth: 0, borderRadius: designTokens.radius.surface, bgcolor: designTokens.color.sidebarSurface }}>
    <Box ref={viewportRef} role="region" aria-label="확정된 결재 흐름" tabIndex={0} data-testid="workflow-approval-progress-flow"
      aria-description="확대한 결재선은 마우스로 드래그하거나 스크롤하여 이동할 수 있습니다. 키보드 방향키로도 이동할 수 있습니다."
      onPointerDown={event => {
        // 터치는 브라우저의 기본 스와이프를 유지하고 마우스 왼쪽 버튼만 이동에 사용합니다.
        if (event.pointerType !== 'mouse' || !event.isPrimary || event.button !== 0 || !canPan) return;
        if ((event.target as Element).closest('button, a, input, textarea, select, [role="button"], [contenteditable="true"]')) return;
        const viewport = event.currentTarget;
        const bounds = viewport.getBoundingClientRect();
        // 스크롤바를 누른 경우에는 브라우저의 기본 조작을 유지합니다.
        if (event.clientX - bounds.left >= viewport.clientWidth || event.clientY - bounds.top >= viewport.clientHeight) return;
        event.preventDefault();
        viewport.focus({ preventScroll: true });
        pan.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, left: viewport.scrollLeft, top: viewport.scrollTop };
        viewport.setPointerCapture(event.pointerId);
        setDragging(true);
      }}
      onPointerMove={event => {
        const start = pan.current;
        if (!start || start.pointerId !== event.pointerId) return;
        if (!(event.buttons & 1)) { endPan(event); return; }
        event.preventDefault();
        event.currentTarget.scrollLeft = start.left + start.x - event.clientX;
        event.currentTarget.scrollTop = start.top + start.y - event.clientY;
      }}
      onPointerUp={endPan} onPointerCancel={endPan} onLostPointerCapture={endPan}
      sx={{ minWidth: 0, height: ready ? Math.min(maximumHeight, Math.ceil(size.height * scale)) : maximumHeight, overflow: 'auto', overscrollBehavior: 'contain', cursor: canPan ? dragging ? 'grabbing' : 'grab' : undefined, userSelect: dragging ? 'none' : undefined, borderRadius: designTokens.radius.surface, bgcolor: designTokens.color.sidebarSurface }}>
      <Box sx={{ position: 'relative', width: ready ? Math.ceil(size.width * scale) : '100%', height: ready ? Math.ceil(size.height * scale) : maximumHeight, mx: 'auto', overflow: 'hidden' }}>
        <Box ref={contentRef} sx={{ position: 'absolute', top: 0, left: 0, boxSizing: 'border-box', width: { xs: size.viewportWidth || '100%', md: 'max-content' }, p: 1.25, transform: `scale(${scale})`, transformOrigin: 'top left' }}>
          {children}
        </Box>
      </Box>
    </Box>
    <Stack direction="row" sx={{ justifyContent: 'flex-end', p: 1, pt: 0.5 }}>
      <Stack role="group" aria-label="결재선 보기 도구" direction="row" sx={{ alignItems: 'center', flexShrink: 0, p: 0.25, bgcolor: designTokens.color.canvas, border: `1px solid ${designTokens.color.divider}`, borderRadius: designTokens.radius.button, boxShadow: designTokens.shadow.surface }}>
        <Tooltip title="전체 보기"><Box component="span" sx={{ display: 'inline-flex' }}>
          <IconButton size="small" color={view.fit ? 'primary' : 'default'} aria-label="결재선 전체 보기" aria-pressed={view.fit}
            sx={{ bgcolor: view.fit ? designTokens.color.selectedNavigationSurface : undefined }}
            disabled={!ready} onClick={() => setView({ fit: true, scale: 1 })}><CenterFocusStrongOutlined fontSize="small" /></IconButton>
        </Box></Tooltip>
        <Tooltip title="축소"><Box component="span" sx={{ display: 'inline-flex' }}>
          <IconButton size="small" aria-label="결재선 축소" disabled={!ready || scale <= minimumScale} onClick={() => zoomTo((percentage - 10) / 100)}><RemoveOutlined fontSize="small" /></IconButton>
        </Box></Tooltip>
        <Tooltip title="확대"><Box component="span" sx={{ display: 'inline-flex' }}>
          <IconButton size="small" aria-label="결재선 확대" disabled={!ready || scale >= maximumScale} onClick={() => zoomTo((percentage + 10) / 100)}><AddOutlined fontSize="small" /></IconButton>
        </Box></Tooltip>
        <Box component="span" role="status" aria-label="결재선 확대 비율" aria-live="polite" aria-atomic="true"
          sx={{ position: 'absolute', width: '1px', height: '1px', p: 0, m: '-1px', overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', border: 0 }}>{percentage}%</Box>
      </Stack>
    </Stack>
  </Box>;
}
